import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Shield, RefreshCw, Lock, Zap, Clock, AlertTriangle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteMaintenance = () => {
  const faqs = [
    {
      question: "What is lawn care website maintenance?",
      answer: "Lawn care website maintenance includes regular updates, security patches, backups, performance monitoring, and content changes. It keeps your site secure, fast, and functional—so you don't lose leads to downtime or outdated information."
    },
    {
      question: "How much does lawn care website maintenance cost?",
      answer: "Standalone maintenance typically costs $50–$150/month. Our Core Website plan at $200/month includes maintenance—along with hosting, updates, and support."
    },
    {
      question: "What happens if I don't maintain my lawn care website?",
      answer: "Without maintenance, your site becomes vulnerable to security breaches, may slow down over time, and can display outdated information. This hurts rankings, conversions, and customer trust."
    },
    {
      question: "Is website maintenance included in your subscription?",
      answer: "Yes. Our Core Website plan at $200/month includes hosting, security updates, backups, performance monitoring, and content changes. No separate maintenance fee."
    },
    {
      question: "How often should my lawn care website be updated?",
      answer: "Security updates should happen monthly or more frequently. Content updates (services, photos, service areas) should happen as your business changes. Seasonal updates are common for lawn care businesses."
    },
    {
      question: "Can I update my own lawn care website?",
      answer: "DIY builders let you update, but you're responsible for security and backups. With our subscription, we handle technical updates and you can request content changes anytime."
    },
    {
      question: "What's included in website maintenance?",
      answer: "Typical maintenance includes: security patches, plugin/software updates, backups, uptime monitoring, performance optimization, SSL certificate renewal, and content updates upon request."
    },
    {
      question: "How do I know if my current site needs maintenance?",
      answer: "Signs include: slow load times, outdated content, security warnings in browsers, broken forms, or your developer being unresponsive. Regular maintenance prevents these issues."
    },
    {
      question: "Do you maintain websites you didn't build?",
      answer: "We focus on subscription clients where we build and maintain. For existing sites, we recommend a full rebuild under our subscription model for best results and ongoing support."
    },
    {
      question: "What's the difference between maintenance and hosting?",
      answer: "Hosting keeps your site online (server space). Maintenance keeps it secure, updated, and running well. Both are included in our subscription—you don't need to manage either separately."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-maintenance/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website maintenance services for lawn care companies included in our subscription model."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-maintenance/#webpage",
    "name": "Lawn Care Website Maintenance | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-maintenance/",
    "description": "Lawn care website maintenance keeps your site secure, fast, and updated. Included in our $200–$300/mo subscription. No separate fees.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-maintenance/#service",
    "name": "Lawn Care Website Maintenance",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Ongoing website maintenance for lawn care companies including security updates, backups, and content changes.",
    "areaServed": "United States",
    "serviceType": "Website Maintenance"
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Maintenance | Leaf Rush"
        description="Lawn care website maintenance keeps your site secure, fast, and updated. Included in our $200–$300/mo subscription. No separate fees."
        keywords="lawn care website maintenance, lawn care site maintenance, lawn care website updates, lawn care website security"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-maintenance/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-maintenance/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Included in Subscription
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website Maintenance That Keeps Your Site Running
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Security updates, backups, performance monitoring, and content changes—all included in your monthly subscription. No separate maintenance fees.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Lawn care website maintenance includes security updates, backups, and content changes. Standalone maintenance costs $50–$150/month. Our subscription includes maintenance in the $200–$300/month price.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're looking for a complete website solution, visit our main{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What's Included in Website Maintenance
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Security Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Regular patches and security monitoring to protect against vulnerabilities and hacks.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <RefreshCw className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Regular Backups</p>
                    <p className="text-sm text-muted-foreground">
                      Automated backups so your site can be restored quickly if anything goes wrong.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Performance Monitoring</p>
                    <p className="text-sm text-muted-foreground">
                      Speed optimization and uptime monitoring to keep your site fast and available.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Clock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Content Updates</p>
                    <p className="text-sm text-muted-foreground">
                      Request changes to services, photos, service areas, or any content—we handle it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Maintenance Matters */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Lawn Care Websites Need Ongoing Maintenance
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-destructive/20 bg-destructive/5">
                  <CardHeader>
                    <AlertTriangle className="h-8 w-8 text-destructive mb-2" />
                    <CardTitle className="text-lg">Without Maintenance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Security vulnerabilities accumulate</li>
                      <li>• Site slows down over time</li>
                      <li>• Outdated info loses trust</li>
                      <li>• Forms may break silently</li>
                      <li>• Rankings can drop</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <Shield className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">With Maintenance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Security patches applied regularly</li>
                      <li>• Site stays fast and optimized</li>
                      <li>• Content stays current</li>
                      <li>• Issues caught and fixed quickly</li>
                      <li>• Rankings stay stable</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Maintenance Cost Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Monthly Cost</th>
                      <th className="text-left py-3 px-4">Includes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">$200–$300 (all-in)</td>
                      <td className="py-3 px-4">Design + Hosting + Maintenance + Support</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Standalone Maintenance</td>
                      <td className="py-3 px-4">$50–$150</td>
                      <td className="py-3 px-4">Updates + Security only</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY (Self-Managed)</td>
                      <td className="py-3 px-4">$0 (your time)</td>
                      <td className="py-3 px-4">You handle everything</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/lawn-care-website-cost/" className="text-primary underline hover:no-underline">
                  lawn care website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Lawn Care Website Maintenance
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional lawn care websites.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for lawn care sites.</p>
                </NavLink>
                <NavLink to="/service-area-pages-local-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Service Area Pages</h3>
                  <p className="text-sm text-muted-foreground">Rank in multiple cities with service area pages.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for Worry-Free Website Maintenance?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes complete website maintenance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteMaintenance;
